import {
  Button,
  ButtonGroup,
  Popover,
  useDisclosure,
  Fieldset,
  CheckboxGroup,
  For,
  Checkbox,
  Stack,
  HStack,
  Field,
  Text,
  Box,
} from "@chakra-ui/react";
import { t } from "i18next";
import { observer } from "mobx-react";
import { useState, useEffect } from "react";
import {
  emptyHlaAttributeFilter,
  MinMaxFilter,
} from "../../../../../map/models/MapFilters";
import { useStores } from "../../../../../shared/stores/RootStore";
import { CustomPopover } from "../../../../../shared/views/CustomPopover";
import { AddFilterButton } from "../AddFilterButton";
import { HlaWayType } from "../../../../../ra/models/HlaWay";
import {
  DistanceUnit,
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  HeavyVehicleFlowType,
  NumberOfLanesType,
} from "@keita/lanepatrol-sdk";
import {
  booleanOptions,
  generateOptions,
  SelectOption,
} from "../../../../../ra/views/HlaWayDetail/form/utils";
import { NumberField } from "../../../../../ra/views/HlaWayDetail/form/NumberField";
import { PresenceDistance } from "../../../../../ra/models/HlaLane";

const HlaAttributeFiltersPopoverBase = () => {
  const { filterStore, projectStore } = useStores();
  const { open, onToggle, onClose } = useDisclosure();
  const [newFilters, setNewFilters] = useState(emptyHlaAttributeFilter());

  useEffect(() => {
    if (open) {
      setNewFilters({ ...filterStore.hlaAttributeFilters });
    }
    //eslint-disable-next-line
  }, [open]);

  const handleSubmit = () => {
    filterStore.setHlaAttributeFilters(newFilters);
    onClose();
  };

  return (
    <>
      <CustomPopover
        open={open}
        onClose={onClose}
        maxH="460px"
        minW="560px"
        trigger={<AddFilterButton label="Attribute" onClick={onToggle} />}
      >
        <Popover.Header>Filter by attribute</Popover.Header>
        <Popover.Body overflowY="auto">
          <Stack gap={6}>
            <CheckboxOptions
              label="Type"
              options={[...Object.values(HlaWayType)].map((type) => ({
                label: t(`shared:hlaWayType.${type}`),
                value: type,
              }))}
              selectedValues={newFilters.type}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  type: checked
                    ? [...prev.type, value]
                    : prev.type.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Heavy vehicle flow"
              options={generateOptions(Object.values(HeavyVehicleFlowType))}
              selectedValues={newFilters.heavyVehicleFlow}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  heavyVehicleFlow: checked
                    ? [...prev.heavyVehicleFlow, value]
                    : prev.heavyVehicleFlow.filter((t) => t !== value),
                }))
              }
            />

            <MinMaxField
              label={t("ra:simulatorInputs.speed", {
                unit:
                  projectStore.distanceUnit === DistanceUnit.Imperial
                    ? "mph"
                    : "km/h",
              })}
              value={newFilters.averageRoadSpeed}
              onChange={(callback) => {
                setNewFilters((prev) => ({
                  ...prev,
                  averageRoadSpeed: callback(prev.averageRoadSpeed),
                }));
              }}
            />

            <MinMaxField
              label="Average daily traffic"
              value={newFilters.averageDailyTraffic}
              onChange={(callback) => {
                setNewFilters((prev) => ({
                  ...prev,
                  averageDailyTraffic: callback(prev.averageDailyTraffic),
                }));
              }}
            />

            <CheckboxOptions
              label="Street lighting"
              options={booleanOptions}
              selectedValues={newFilters.lit}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  lit: checked
                    ? [...prev.lit, value]
                    : prev.lit.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Facility type"
              options={generateOptions(Object.values(FacilityType))}
              selectedValues={newFilters.facilityType}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  facilityType: checked
                    ? [...prev.facilityType, value]
                    : prev.facilityType.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Flow direction"
              options={generateOptions(Object.values(FlowDirectionType))}
              selectedValues={newFilters.flowDirection}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  flowDirection: checked
                    ? [...prev.flowDirection, value]
                    : prev.flowDirection.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Facility width per direction"
              options={generateOptions(Object.values(FacilityWidth))}
              selectedValues={newFilters.facilityWidthPerDirection}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  facilityWidthPerDirection: checked
                    ? [...prev.facilityWidthPerDirection, value]
                    : prev.facilityWidthPerDirection.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Tram or train rails"
              options={booleanOptions}
              selectedValues={newFilters.tramOrTrainRails}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  tramOrTrainRails: checked
                    ? [...prev.tramOrTrainRails, value]
                    : prev.tramOrTrainRails.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Light segregation"
              options={booleanOptions}
              selectedValues={newFilters.lightSegregation}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  lightSegregation: checked
                    ? [...prev.lightSegregation, value]
                    : prev.lightSegregation.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Hard segregation"
              options={booleanOptions}
              selectedValues={newFilters.hardSegregation}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  hardSegregation: checked
                    ? [...prev.hardSegregation, value]
                    : prev.hardSegregation.filter((t) => t !== value),
                }))
              }
            />

            <Text>Adjacent features</Text>

            <CheckboxOptions
              label="Road lane"
              options={presenceDistanceOptions}
              selectedValues={newFilters.roadLane}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  roadLane: checked
                    ? [...prev.roadLane, value]
                    : prev.roadLane.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Number of lanes"
              options={generateOptions(Object.values(NumberOfLanesType))}
              selectedValues={newFilters.numberOfLanes}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  numberOfLanes: checked
                    ? [...prev.numberOfLanes, value]
                    : prev.numberOfLanes.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Parking"
              options={presenceDistanceOptions}
              selectedValues={newFilters.vehicleParking}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  vehicleParking: checked
                    ? [...prev.vehicleParking, value]
                    : prev.vehicleParking.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Sidewalk"
              options={presenceDistanceOptions}
              selectedValues={newFilters.sidewalk}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  sidewalk: checked
                    ? [...prev.sidewalk, value]
                    : prev.sidewalk.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Object or level change"
              options={presenceDistanceOptions}
              selectedValues={newFilters.objectOrLevelChange}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  objectOrLevelChange: checked
                    ? [...prev.objectOrLevelChange, value]
                    : prev.objectOrLevelChange.filter((t) => t !== value),
                }))
              }
            />

            <CheckboxOptions
              label="Severe hazard"
              options={presenceDistanceOptions}
              selectedValues={newFilters.severeHazard}
              onChange={(value, checked) =>
                setNewFilters((prev) => ({
                  ...prev,
                  severeHazard: checked
                    ? [...prev.severeHazard, value]
                    : prev.severeHazard.filter((t) => t !== value),
                }))
              }
            />
          </Stack>
        </Popover.Body>
        <Popover.Footer display="flex" justifyContent="flex-end">
          <ButtonGroup colorPalette={"primary"} size={"sm"}>
            <Button onClick={() => setNewFilters(emptyHlaAttributeFilter())}>
              {t("map:filters.clearButton")}
            </Button>
            <Button onClick={handleSubmit}>
              {t("map:filters.submitButton")}
            </Button>
          </ButtonGroup>
        </Popover.Footer>
      </CustomPopover>
    </>
  );
};

export const HlaAttributeFiltersPopover = observer(
  HlaAttributeFiltersPopoverBase,
);

const CheckboxOptions = ({
  selectedValues,
  options,
  label,
  onChange,
}: {
  selectedValues: any[];
  options: SelectOption;
  label: string;
  onChange: (value: any, checked: boolean) => void;
}) => {
  return (
    <Fieldset.Root>
      <CheckboxGroup>
        <Fieldset.Legend fontSize="sm" mb="2">
          {label}
        </Fieldset.Legend>
        <Fieldset.Content flexDir="row" flexWrap={"wrap"} gap={2}>
          <For each={options}>
            {(option) => (
              <Checkbox.Root
                key={option.value}
                checked={selectedValues.includes(option.value)}
                onCheckedChange={({ checked }) => {
                  onChange(option.value, !!checked);
                }}
                colorPalette={"primary"}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label fontWeight={"normal"} fontSize={"xs"}>
                  {option.label}
                </Checkbox.Label>
              </Checkbox.Root>
            )}
          </For>
        </Fieldset.Content>
      </CheckboxGroup>
    </Fieldset.Root>
  );
};

const MinMaxField = ({
  value,
  label,
  onChange,
}: {
  value: MinMaxFilter;
  label: string;
  onChange: (callback: (prev: MinMaxFilter) => MinMaxFilter) => void;
}) => {
  return (
    <Field.Root>
      <Field.Label>{label}</Field.Label>
      <HStack width={"full"}>
        <Box flex={1}>
          <NumberField
            placeholder={"Min"}
            value={value.min}
            onChange={(value: number | null) => {
              onChange((prev) => ({ ...prev, min: value }));
            }}
          />
        </Box>

        <Text>to</Text>

        <Box flex={1}>
          <NumberField
            placeholder={"Max"}
            value={value.max}
            onChange={(value: number | null) => {
              onChange((prev) => ({ ...prev, max: value }));
            }}
          />
        </Box>
      </HStack>
    </Field.Root>
  );
};

const presenceDistanceOptions = [
  { value: null, label: "Not present" },
  { value: PresenceDistance.From0to1m, label: "0 to 1m" },
  { value: PresenceDistance.From1to3m, label: "1 to 3m" },
];
